import { CircularProgress, Stack } from '@mui/material';
import get from 'lodash-es/get';
import React, { ReactNode, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useConfig } from '../../../../hooks/useConfig';
import { useConstants } from '../../../../hooks/useConstants';
import MediaPreview from './Link/MediaPreview';
import PreviewControls from './Link/PreviewControls';
import UploadHandler from './Link/UploadHandler';

interface LinkProps {
  fieldName?: string;
  s3InfoUrl?: string;
  accept?: string;
  helpText?: ReactNode;
  onSuccess?: Function;
  onVideoLoadedData?: (duration: number, isUploaded?: boolean ) => void;
  onRemoveSuccess?: Function;
  optional?: boolean;
}

export default function Link({
  fieldName = 'link',
  s3InfoUrl = '/admin/helpers/s3_upload_info/',
  accept = '.png,.jpg,.jpeg,.gif',
  helpText,
  onSuccess,
  onVideoLoadedData,
  onRemoveSuccess,
  optional = false,
}: LinkProps) {
  const { apiHost } = useConfig();
  const { watch, setValue, setError } = useFormContext();
  const { constants } = useConstants();
  const [displayState, setDisplayState] = useState({
    showPreview: false,
    isLoading: false,
  });

  const THINKNEAR_HOSTED_PREFIX_CREATIVES = get(constants, 'CREATIVE.THINKNEAR_HOSTED_PREFIX_CREATIVES');

  const generateKey = () => `${THINKNEAR_HOSTED_PREFIX_CREATIVES}creative_1${new Date().getTime()}`;

  const watchedLink = watch(fieldName);
  const isVideo = watchedLink?.toLowerCase().endsWith('.mp4');
  const campaignId = watch('campaignId') || watch('adGroupId');
  const creativeId = watch('id');

  const handleUpload = {
    onStart: () => {
      setDisplayState((prev) => ({ ...prev, isLoading: true, showPreview: false }));
    },

    onSuccess: (links: string, path: string, files: File[]) => {
      // Videos use s3_get_resource with just the S3 key; images use the full public URL
      const isVideoFile = files?.[0]?.name?.toLowerCase().endsWith('.mp4');
      setValue(fieldName, isVideoFile ? path : links, { shouldDirty: true });
      setDisplayState((prev) => ({ ...prev, isLoading: false }));

      if (files?.[0] && !watch('name')) {
        const fileNameWithoutExtension = files[0].name.replace(/\.[^/.]+$/, '');
        setValue('name', fileNameWithoutExtension, { shouldDirty: true });
      }

      onSuccess?.(links, path, files);
    },

    onFailure: (error: any) => {
      if (!optional) {
        setError(fieldName, {
          type: 'custom',
          message: error?.message || 'Upload failed',
        });
      }
      setDisplayState((prev) => ({ ...prev, isLoading: false }));
    },
  };

  const handleRemove = () => {
    setValue(fieldName, null, { shouldDirty: true });
    setValue('uploadType', null, { shouldDirty: true });
    setDisplayState((prev) => ({ ...prev, showPreview: false }));
    onRemoveSuccess?.();
  };

  return (
    <Stack spacing={2}>
      {!watchedLink ? (
        <UploadHandler
          name={fieldName}
          s3InfoUrl={s3InfoUrl}
          generateKey={generateKey}
          accept={accept}
          onStart={handleUpload.onStart}
          onSuccess={handleUpload.onSuccess}
          onFailure={handleUpload.onFailure}
          label={helpText}
        />
      ) : (
        <Stack direction="column" alignItems="flex-start" spacing={2}>
          <MediaPreview
            url={watchedLink}
            isVideo={isVideo}
            showPreview={displayState.showPreview}
            onLoad={() => setDisplayState((prev) => ({ ...prev, showPreview: true }))}
            onVideoLoadedData={onVideoLoadedData}
            apiHost={apiHost}
          />

          <PreviewControls
            url={watchedLink}
            onRemove={handleRemove}
            campaignId={campaignId}
            creativeId={creativeId}
          />
        </Stack>
      )}
      {displayState.isLoading && <CircularProgress />}
    </Stack>
  );
}
